import { useMemo } from 'react'
import { ReturnFormData } from '../../components/EditReturnItemModal'

interface OrderItem {
  id?: number
  quantity?: number
  product?: {
    id?: number
    name?: string
    requiredSerialNumbers?: boolean
  }
  clearanceItems?: any[]
}

interface ProductMove {
  orderItem?: {
    id?: number
  }
  product?: {
    id?: number
  }
  amount?: number
}

interface UseReturnQuantitiesProps {
  orderItems: OrderItem[]
  returnedProductMoves?: ProductMove[]
  activeReturns: ReturnFormData[]
}

interface ItemWithQuantities extends OrderItem {
  quantityReturned: number
  quantityAvailableToReturn: number
}

export const useReturnQuantities = ({ orderItems, returnedProductMoves, activeReturns }: UseReturnQuantitiesProps) => {
  const orderItemsWithQuantities = useMemo<ItemWithQuantities[]>(() => {
    return orderItems.map(item => {
      // Calculate quantity already returned. Bundle children share the same order item,
      // so match by product as well when both sides know it.
      const returnedProductMovesForItem = returnedProductMoves?.filter(
        move =>
          move.orderItem?.id &&
          item.id &&
          move.orderItem.id === item.id &&
          (!move.product?.id || !item.product?.id || move.product.id === item.product.id),
      )
      const quantityReturned = returnedProductMovesForItem?.reduce((sum, move) => sum + (move.amount || 0), 0) || 0

      // Calculate quantity in active returns (not yet processed)
      const quantityInActiveReturns =
        activeReturns
          .filter(
            r =>
              item.id &&
              r.orderItemId === item.id &&
              (!item.product?.id || !r.productId || r.productId === item.product.id),
          )
          .reduce((sum, r) => sum + r.amount, 0) || 0

      // Calculate available to return
      const quantityAvailableToReturn = Math.max(0, (item.quantity || 0) - quantityReturned - quantityInActiveReturns)

      return {
        ...item,
        quantityReturned,
        quantityAvailableToReturn,
      }
    })
  }, [orderItems, returnedProductMoves, activeReturns])

  const orderItemsAvailableToReturn = useMemo(() => {
    return orderItemsWithQuantities.filter(item => item.quantityAvailableToReturn > 0)
  }, [orderItemsWithQuantities])

  const hasItemsToReturn = orderItemsAvailableToReturn.length > 0
  const totalItemsToReturn = orderItemsAvailableToReturn.reduce((sum, item) => sum + item.quantityAvailableToReturn, 0)

  return {
    orderItemsWithQuantities,
    orderItemsAvailableToReturn,
    hasItemsToReturn,
    totalItemsToReturn,
  }
}
